import { useEffect, useMemo, useRef, useState } from 'react';
import type { UserResponse } from 'stream-chat';

import type Account from '@/data/account/model';
import { getAuthAccount } from '@/shared/auth/selectors';
import Tooltip from '@/shared/ui/Tooltip';
import { useWiwSelector } from '@/store';
import { STREAM_CHANNEL_TYPES } from '@/workchat/constants';
import { getStreamUserFirstLastNames } from '@/workchat/utils';

import '@/workchat/v3/components/ConversationTitleV3/ConversationTitleV3.scss';

type ConversationTitleProps = {
  conversationName?: string;
  conversationType: STREAM_CHANNEL_TYPES;
  channelMembers?: UserResponse[];
  inConversation?: boolean;
};

type getTitleParamsType = {
  account: Account;
  inConversation?: boolean;
  channelMembers?: UserResponse[];
  conversationName?: string;
  conversationType?: STREAM_CHANNEL_TYPES;
};

export function getTitle({
  account,
  inConversation,
  channelMembers,
  conversationName,
  conversationType,
}: getTitleParamsType) {
  if (conversationName) {
    return conversationName;
  }

  // Helper function to get participant names
  const getParticipantNames = () => {
    if (!channelMembers) {
      return '';
    }

    return channelMembers
      .map(user => getStreamUserFirstLastNames(user).firstName)
      .filter(firstName => !!firstName)
      .join(', ');
  };

  switch (conversationType) {
    case STREAM_CHANNEL_TYPES.BROADCAST:
      return account.company || 'Company WorkChat';
    default:
      return inConversation ? 'Group WorkChat' : getParticipantNames();
  }
}

export default function ConversationTitleV3({
  conversationName,
  conversationType,
  channelMembers,
  inConversation,
}: ConversationTitleProps) {
  const account = useWiwSelector(getAuthAccount);

  const [hasTooltip, setHasTooltip] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const title = useMemo(
    () => getTitle({ account, inConversation, channelMembers, conversationName, conversationType }),
    [account, inConversation, channelMembers, conversationName, conversationType],
  );

  useEffect(() => {
    const div = ref.current;

    if (!div || !div.scrollWidth || !div.offsetWidth) {
      return;
    }

    const hasScroll = div.offsetWidth < div.scrollWidth;
    setHasTooltip(hasScroll);
  }, [ref]);

  const renderConversationTitle = () => {
    if (inConversation && hasTooltip) {
      return (
        <Tooltip label={title} position="bottom">
          <div ref={ref}>{title}</div>
        </Tooltip>
      );
    }
    return (
      <div className="ConversationTitleV3" ref={ref}>
        {title}
      </div>
    );
  };

  return renderConversationTitle();
}
